/**
 * プレビュー機能専用のポップアップ制御ヘルパー
 * 
 * 【背景・必要性】
 * 管理画面のポップアッププレビュー機能では、以下の問題が発生していました：
 * 1. PC用iframe・SP用iframeの2つが同時に読み込まれる
 * 2. 各iframeでStimulus Application.start()が実行され、PopupControllerが複数回初期化される  
 * 3. 結果として同じポップアップが複数回表示され、ユーザーが複数回バツボタンを押す必要があった
 * 
 * 【解決アプローチ】
 * Application.start()の重複実行を防ぐのではなく、ポップアップの表示制御で解決：
 * - トップレベルウィンドウ（window.top）でPC/SP別々の表示状態を管理
 * - 各viewport（PC/SP）で最初の1回のみポップアップを表示し、2回目以降はスキップ
 * - プレビュー環境でのサーバーAPI呼び出し（クッキー設定）も無効化
 * 
 * 【技術仕様】
 * - iframe判定: window.frameElement && pathname.includes('preview_iframe')
 * - 状態管理: window.top.previewPopupShown = { 'viewport_pc': true, 'viewport_sp': true }
 * - URL判定: URLSearchParams('viewport')でPC/SP識別
 * - API無効化: hostname.startsWith('admin.')でプレビュー環境判定
 */
export class PreviewPopupHelper {
  constructor(popupController) {
    this.controller = popupController;
  }

  /**
   * プレビューモードかどうかを判定
   * 
   * 判定条件：
   * 1. iframe内で実行されている（window.frameElement存在）
   * 2. iframeタグである（tagName === 'IFRAME'）
   * 3. プレビューページである（pathname.includes('preview_iframe')）
   * 
   * @returns {boolean} true: プレビューモード, false: 通常モード
   */
  isPreviewMode() {
    return window.frameElement && 
           window.frameElement.tagName === 'IFRAME' && 
           window.location.pathname.includes('preview_iframe');
  }

  /**
   * プレビューモードでポップアップ表示を制御すべきかチェック
   * 
   * 処理フロー：
   * 1. 通常モード → 常に表示継続
   * 2. プレビューモード → 表示済みチェック
   *   - 未表示 → 表示済みマーク後、表示継続  
   *   - 表示済み → 表示スキップ
   * 
   * これにより同一viewport内での重複表示を防止
   * 
   * @returns {boolean} true: 表示継続, false: 表示スキップ
   */
  shouldShowPopup() {
    if (!this.isPreviewMode()) {
      return true; // プレビューモードでない場合は常に表示
    }

    if (this.isPopupAlreadyShown()) {
      console.log('Preview: Popup already shown, skipping...');
      return false;
    }

    this.markPopupAsShown();
    return true;
  }

  /**
   * 現在のviewport（PC/SP）で既にポップアップが表示されているかチェック
   * 
   * 状態管理の仕様：
   * - window.top.previewPopupShown[viewportKey] でPC/SP別々に管理
   * - viewportKey例: 'viewport_pc', 'viewport_sp'
   * - 複数のiframeが読み込まれてもトップレベルウィンドウで一元管理
   * 
   * @returns {boolean} true: 表示済み, false: 未表示
   */
  isPopupAlreadyShown() {
    const viewportKey = this.getViewportKey();
    if (!window.top.previewPopupShown) {
      window.top.previewPopupShown = {};
    }
    return window.top.previewPopupShown[viewportKey] === true;
  }

  /**
   * 現在のviewportでポップアップ表示済みとしてマーク
   */
  markPopupAsShown() {
    const viewportKey = this.getViewportKey();
    if (!window.top.previewPopupShown) {
      window.top.previewPopupShown = {};
    }
    window.top.previewPopupShown[viewportKey] = true;
    console.log('Preview: Popup marked as shown for ' + viewportKey);
  }

  /**
   * 現在のviewport識別キーを取得（PC/SP別管理用）
   * @returns {string}
   */
  getViewportKey() {
    const urlParams = new URLSearchParams(window.location.search);
    const viewport = urlParams.get('viewport') || 'pc';
    return 'viewport_' + viewport;
  }

  /**
   * プレビューモードでサーバーAPI呼び出しを無効化すべきかチェック
   * 
   * 無効化理由：
   * - プレビュー環境（admin.サブドメイン）では実際のクッキー操作は不要
   * - 開発用途のため、本番DBへの不要なアクセスを防止
   * - fetch('/popups/dismiss')の実行を防ぎ、ログノイズも削減
   * 
   * @returns {boolean} true: API呼び出しを無効化, false: 通常通り実行
   */
  shouldSkipServerRequest() {
    // adminサブドメインで実行している場合はfetchしない（プレビューモード）
    if (window.location.hostname.startsWith('admin.')) {
      console.log('Preview mode: Cookie operation skipped');
      return true;
    }
    return false;
  }
}